import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

// Import reusable components
import { TableRow, TableCell } from "@/components/ui/table";
import TableActionsMenu from "@/components/ui/TableActionsMenu";

// Import utils and helpers
import { searchFilterCustomers } from "../utils/searchFilterCustomers";
import { STATUS_STYLES } from "@/utils/statusStyles";
import { fetchCustomers } from "@/services/peopleApi";
import { fetchOrdersList } from "@/services/ordersApi";
import { useBusiness } from "@hooks/useBusiness";
import { useStorePath } from "@hooks/useStorePath";

export default function useCustomersTable() {
  const { businessId } = useBusiness();

  // Orders come along so each row can show a total-orders count and last
  // order date - the customer document itself has no order history.
  const { data: orders = [] } = useQuery({
    queryKey: ["orders", businessId],
    queryFn: () => fetchOrdersList(businessId),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: customers = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["customers", businessId, orders.length],
    queryFn: () => fetchCustomers(businessId, orders),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  // implement persisitence of search and filters
  // capture states for the search and filter functionality
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "All";

  const navigate = useNavigate();
  const { storePath } = useStorePath();

  const updateSearchTerm = useCallback((val) => {
    const newParams = new URLSearchParams(searchParams);

    if (val) {
        newParams.set("search", val);
    } else {
        newParams.delete("search");
    }
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const updateStatusFilter = useCallback((val) => {
    const newParams = new URLSearchParams(searchParams);

    if (val && val !== "All") {
      newParams.set("status", val);
    } else {
      newParams.delete("status");
    }

    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // TODO: Replace with the backend call that accepts status and search as params
  // Memoize the filtered data
  const filteredCustomers = useMemo(() => {
      return searchFilterCustomers(customers, searchTerm, statusFilter);
  }, [customers, searchTerm, statusFilter]);

  // Route to view/edit customer page
  const handleView = (customer) => {
    if (!customer?.id) {
      console.error("Missing customer ID");
      return;
    }

    navigate(storePath(`customers/${customer.id}`));
  };

  // Memoize customerColumns to prevent unecessary table re-renders
  const customerColumns = useMemo(() => [
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          className="rounded"
          checked={table.getIsAllPageRowsSelected()}
          onChange={(e) => table.toggleAllPageRowsSelected(!!e.target.checked)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="rounded"
          checked={row.getIsSelected()}
          onChange={(e) => row.toggleSelected(!!e.target.checked)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: "customerNumber",
      header: "Customer No.",
      cell: ({ row }) => row.original?.customerNumber || <span className="text-gray-400 italic">N/A</span>,
    },

    {
      id: "name",
      header: "Name",
      accessorFn: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim(),
      cell: ({ row }) => {
        const firstName = row.original?.firstName || "";
        const lastName = row.original?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || <span className="text-gray-400 italic">Unnamed</span>;
      },
    },

    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => row.original?.email || <span className="text-gray-400 italic">Unknown</span>,
    },

    {
      accessorKey: "phone",
      header: "Phone",
      cell: ({ row }) => row.original?.phone || <span className="text-gray-400 italic">N/A</span>,
    },

    {
      accessorKey: "totalOrders",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Total Orders
          {column.getIsSorted() === "asc" && <span>↑</span>}
          {column.getIsSorted() === "desc" && <span>↓</span>}
        </button>
      ),
      cell: ({ row }) => {
        const orders = row.original?.totalOrders;
        return typeof orders === "number"
          ? orders
          : <span className="text-gray-400 italic">N/A</span>;
      }
    },

    {
      accessorKey: "lastOrder",
      header: "Last Ordered",
      cell: ({ row }) => {
        const rawDate = row.original?.lastOrder;
        if (!rawDate) return <span className="text-gray-400 italic">No Orders</span>;

        const date = new Date(rawDate);
        const formatted = date.toLocaleDateString("en-KE", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

        return <span>{formatted}</span>;
      },
    },
  ], []);


  // Render each row of the table using the data + column config
  // Memoize to stabilise the function and reduce re-renders or memo mismatch
  const renderCustomerRow = useCallback((row) => {
    const customer = row.original;

    return (
      <TableRow key={row.id} onClick={() => handleView(customer)} className="cursor-pointer hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell className="px-4 py-3 text-[13px] font-medium" key={cell.id}>
            {cell.getContext().column.columnDef.cell
              ? cell.getContext().column.columnDef.cell(cell.getContext())
              : cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );  
  }, [handleView]);

  return {
    allCustomers: customers,
    customers: filteredCustomers,
    searchTerm,
    updateSearchTerm,
    statusFilter,
    updateStatusFilter,
    refetch,
    customerColumns,
    renderCustomerRow,
    isLoading,
    isError,
  };
};